import React, { useState } from 'react';
import { Scissors, X, Plus, Trash2, GripVertical, CheckCircle2, AlertTriangle } from 'lucide-react';
import { PALETTE, fmtH } from './DecoupageJournees';

const round = (h) => Math.round(h * 2) / 2;
const clone = (days) => days.map((d) => ({ segments: d.segments.map((s) => ({ ...s })) }));

export const DecoupageEditor = ({ days, hpj, modulesFormation, getModuleInfo, onChange }) => {
  const [drag, setDrag] = useState(null);
  const [overDay, setOverDay] = useState(null);

  const colorIdx = Object.fromEntries(modulesFormation.map((mf, i) => [mf.module_id, i % PALETTE.length]));
  const placed = {};
  days.forEach((d) => d.segments.forEach((s) => { placed[s.module_id] = (placed[s.module_id] || 0) + (s.heures || 0); }));
  const restants = modulesFormation
    .map((mf) => ({ module_id: mf.module_id, reste: round((mf.duree_heures || 0) - (placed[mf.module_id] || 0)) }))
    .filter((r) => r.reste !== 0);

  const commit = (next) => onChange(next);

  const updateHeures = (di, si, value) => {
    const next = clone(days);
    next[di].segments[si].heures = Math.max(0, parseFloat(value) || 0);
    commit(next);
  };

  const removeSeg = (di, si) => {
    const next = clone(days);
    next[di].segments.splice(si, 1);
    commit(next);
  };

  const splitSeg = (di, si) => {
    const next = clone(days);
    const seg = next[di].segments[si];
    if (seg.heures < 1) return;
    const part = round(seg.heures / 2);
    seg.heures = round(seg.heures - part);
    if (!next[di + 1]) next.push({ segments: [] });
    next[di + 1].segments.unshift({ module_id: seg.module_id, heures: part });
    commit(next);
  };

  const addSegToDay = (di, moduleId) => {
    const reste = restants.find((r) => r.module_id === moduleId)?.reste || 0;
    if (reste <= 0) return;
    const next = clone(days);
    const total = next[di].segments.reduce((s, x) => s + x.heures, 0);
    const libre = Math.max(0, hpj - total);
    const heures = libre > 0 ? Math.min(libre, reste) : reste;
    const existing = next[di].segments.find((s) => s.module_id === moduleId);
    if (existing) existing.heures = round(existing.heures + heures);
    else next[di].segments.push({ module_id: moduleId, heures });
    commit(next);
  };

  const addDay = () => commit([...clone(days), { segments: [] }]);
  const removeDay = (di) => commit(clone(days).filter((_, i) => i !== di));

  const onDropDay = (di) => {
    if (!drag) return;
    const next = clone(days);
    const [seg] = next[drag.di].segments.splice(drag.si, 1);
    if (drag.di !== di) {
      const existing = next[di].segments.find((s) => s.module_id === seg.module_id);
      if (existing) existing.heures = round(existing.heures + seg.heures);
      else next[di].segments.push(seg);
    } else {
      next[di].segments.push(seg);
    }
    setDrag(null);
    setOverDay(null);
    commit(next);
  };

  return (
    <div className="p-3 space-y-2" data-testid="decoupage-editor">
      <p className="text-[11px] text-slate-500">
        Modifiez les heures de chaque bloc, <Scissors className="w-3 h-3 inline" /> scinde un bloc vers la journée suivante, glissez un bloc vers une autre journée.
      </p>
      <div className="space-y-1.5 max-h-[360px] overflow-y-auto pr-1">
        {days.map((day, di) => {
          const total = day.segments.reduce((s, x) => s + (x.heures || 0), 0);
          const statusCls = total > hpj ? 'text-red-600' : total < hpj ? 'text-amber-600' : 'text-emerald-600';
          const modulesAjoutables = restants.filter((r) => r.reste > 0);
          return (
            <div
              key={di}
              onDragOver={(e) => { e.preventDefault(); setOverDay(di); }}
              onDragLeave={() => setOverDay(null)}
              onDrop={(e) => { e.preventDefault(); onDropDay(di); }}
              className={`flex items-center gap-2 rounded-md border px-2 py-1.5 transition-colors ${overDay === di ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-white'}`}
              data-testid={`jour-edit-${di + 1}`}
            >
              <span className="text-[11px] font-bold text-slate-500 w-7 shrink-0">J{di + 1}</span>
              <div className="flex-1 flex flex-wrap items-center gap-1.5 min-h-[28px]">
                {day.segments.map((seg, si) => {
                  const mod = getModuleInfo(seg.module_id);
                  const color = PALETTE[colorIdx[seg.module_id] ?? 0];
                  return (
                    <div
                      key={si}
                      draggable
                      onDragStart={() => setDrag({ di, si })}
                      onDragEnd={() => { setDrag(null); setOverDay(null); }}
                      className={`flex items-center gap-1 rounded border pl-1 pr-0.5 py-0.5 cursor-grab active:cursor-grabbing ${color.light} ${color.border}`}
                      title={`${mod.code} — ${mod.titre}`}
                      data-testid={`seg-${di + 1}-${si + 1}`}
                    >
                      <GripVertical className="w-3 h-3 opacity-50" />
                      <span className="font-mono text-[10px] font-bold">{mod.code}</span>
                      <span className="text-[10px] max-w-[110px] truncate hidden xl:inline">{mod.titre}</span>
                      <input
                        type="number"
                        value={seg.heures}
                        step="0.5"
                        min="0"
                        onChange={(e) => updateHeures(di, si, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-12 h-5 text-[10px] text-center rounded border border-white/60 bg-white/70 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                        data-testid={`seg-heures-${di + 1}-${si + 1}`}
                      />
                      <span className="text-[10px]">h</span>
                      <button type="button" onClick={() => splitSeg(di, si)} className="p-0.5 rounded hover:bg-white/70" title="Scinder vers la journée suivante" data-testid={`seg-split-${di + 1}-${si + 1}`}>
                        <Scissors className="w-3 h-3" />
                      </button>
                      <button type="button" onClick={() => removeSeg(di, si)} className="p-0.5 rounded hover:bg-white/70" title="Retirer de cette journée" data-testid={`seg-remove-${di + 1}-${si + 1}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
                {modulesAjoutables.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => e.target.value && addSegToDay(di, e.target.value)}
                    className="h-6 text-[10px] rounded border border-dashed border-slate-300 text-slate-500 bg-slate-50 px-1 focus:outline-none"
                    data-testid={`add-module-jour-${di + 1}`}
                  >
                    <option value="">+ module…</option>
                    {modulesAjoutables.map((r) => {
                      const mod = getModuleInfo(r.module_id);
                      return <option key={r.module_id} value={r.module_id}>{mod.code} — {mod.titre} ({fmtH(r.reste)}h restantes)</option>;
                    })}
                  </select>
                )}
              </div>
              <span className={`text-[10px] font-semibold w-9 text-right shrink-0 ${statusCls}`}>{fmtH(total)}h</span>
              <button
                type="button"
                onClick={() => removeDay(di)}
                disabled={day.segments.length > 0}
                className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-300"
                title={day.segments.length > 0 ? 'Videz la journée pour la supprimer' : 'Supprimer la journée'}
                data-testid={`remove-jour-${di + 1}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button type="button" onClick={addDay} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="btn-add-jour">
          <Plus className="w-3 h-3" /> Ajouter une journée
        </button>
        {restants.length === 0 ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium" data-testid="decoupage-complet">
            <CheckCircle2 className="w-3.5 h-3.5" /> Toutes les heures sont placées
          </span>
        ) : (
          <div className="flex flex-wrap items-center gap-1" data-testid="decoupage-restants">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            {restants.map((r) => {
              const mod = getModuleInfo(r.module_id);
              return (
                <span key={r.module_id} className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${r.reste > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                  {mod.code} : {r.reste > 0 ? `${fmtH(r.reste)}h à placer` : `${fmtH(-r.reste)}h en trop`}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
